import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetInventory } from "../../hooks/inventory/useGetInventory";
import { useGetWarehouses } from "../../hooks/warehouses/useGetWarehouses";
import useTableActions from "../../hooks/common/useTableActions";

import TableLayout from "../global/Table/TableLayout";
import ExportButton from "../exports/ExportButton";
import { InventoryTableColumns } from "./Table/InventoryTableColumns";
import InventoryTableRow from "./Table/InventoryTableRow";
import AdjustStockModal from "./AdjustStockModal";

const InventoryTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");

    const [warehouseFilter, setWarehouseFilter] = useState("");
    const [stockFilter, setStockFilter] = useState("");
    const [adjustingStock, setAdjustingStock] = useState(null);

    const { warehouses } = useGetWarehouses({ per_page: 200 });

    const {
        page, perPage, search, order, selectedIds, menuAnchorEl,
        setSearch, handleRequestSort, handleRowCheckboxClick, handleSelectAllClick,
        handleMenuOpen, handleMenuClose, handlePageChange, handlePerPageChange,
    } = useTableActions({ basePath: "/inventory" });

    const params = useMemo(() => ({
        page, per_page: perPage,
        search: search || undefined,
        warehouse_id: warehouseFilter || undefined,
        in_stock: stockFilter === "" ? undefined : stockFilter,
    }), [page, perPage, search, warehouseFilter, stockFilter]);

    const { stocks, meta, isLoading, error, refetch } = useGetInventory(params);

    const warehouseOptions = useMemo(() => ([
        { value: "", label: { ar: "كل المستودعات", en: "All Warehouses" } },
        ...warehouses.map((w) => ({
            value: String(w.id),
            label: { ar: w.name || "-", en: w.name || "-" },
        })),
    ]), [warehouses]);

    const tableFilters = [
        {
            name: "warehouse",
            label: { ar: "المستودع", en: "Warehouse" },
            value: warehouseFilter,
            onChange: setWarehouseFilter,
            options: warehouseOptions,
        },
        {
            name: "in_stock",
            label: { ar: "حالة المخزون", en: "Stock Status" },
            value: stockFilter,
            onChange: setStockFilter,
            options: [
                { value: "",  label: { ar: "الكل", en: "All" } },
                { value: "1", label: { ar: "متوفر", en: "In stock" } },
                { value: "0", label: { ar: "نفذ", en: "Out of stock" } },
            ],
        },
    ];

    const renderRow = (rowParams) => (
        <InventoryTableRow
            key={rowParams.item.id}
            item={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            onAdjust={setAdjustingStock}
        />
    );

    return (
        <>
            <TableLayout
                title={isArabic ? "المخزون" : "Inventory"}
                subtitle={isArabic ? "إدارة كميات المنتجات في المستودعات" : "Manage product stock across warehouses"}
                search={search}
                onSearchChange={setSearch}
                addButtonVisible={false}
                headerExtra={<ExportButton type="inventory" filters={params} variant="contained" size="medium" />}
                isLoading={isLoading}
                error={error}
                items={stocks}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                order={order}
                onRequestSort={handleRequestSort}
                columns={InventoryTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                menuDisableEdit
                menuDisableDelete
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, stocks)}
                onRowCheckboxClick={handleRowCheckboxClick}
                bulkDeleteDisabled
                filters={tableFilters}
                onClearAllFilters={() => {
                    setWarehouseFilter("");
                    setStockFilter("");
                }}
                emptyLabelAr="لا توجد سجلات مخزون"
                emptyLabelEn="No inventory records"
                refetch={refetch}
            />

            <AdjustStockModal
                open={Boolean(adjustingStock)}
                stock={adjustingStock}
                onClose={() => setAdjustingStock(null)}
            />
        </>
    );
};

export default InventoryTable;
